<template>
  <nav class="sticky top-0 z-40 backdrop-blur-xl border-b border-line" style="background: rgba(5,11,26,.7);">
    <div class="max-w-[1400px] mx-auto px-4 md:px-8 py-4 flex items-center justify-between">

      <div class="flex items-center gap-2 lg:gap-6">
        <router-link to="/" class="flex items-center gap-3">
          <div class="w-9 h-9 rounded-xl bg-grad grid place-items-center text-slate-950 font-black">M</div>
          <div class="hidden md:block font-bold tracking-tight">MyFranceAdmin</div>
        </router-link>

        <div class="nav-items hidden xl:flex items-center gap-1 ml-4">
          <router-link
            v-for="item in items" :key="item.to" :to="item.to"
            class="nav-link" active-class="nav-link-active"
          >
            <component :is="item.icon" class="w-4 h-4" />
            {{ item.label }}
          </router-link>

          <MegaMenu label="Demarches" :items="demarchesItems" />
          <MegaMenu label="Vie pratique" :items="vieItems" />
        </div>
      </div>

      <div class="flex items-center gap-2 md:gap-3">
        <button class="cmdk-trigger hidden lg:flex">
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          Rechercher
          <span class="kbd">Cmd K</span>
        </button>

        <div class="hidden md:inline-flex items-center gap-1 p-1 rounded-full bg-white/[.04] border border-line">
          <button class="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-ink text-bg">FR</button>
          <button class="px-2.5 py-1 rounded-full text-[11px] font-semibold text-muted hover:text-ink transition-colors">EN</button>
          <button class="px-2.5 py-1 rounded-full text-[11px] font-semibold text-muted hover:text-ink transition-colors">AR</button>
        </div>

        <IconButton label="Notifications" badge>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
        </IconButton>

        <router-link to="/profil" class="w-10 h-10 rounded-full bg-grad grid place-items-center text-slate-950 font-bold text-sm shrink-0 hover:shadow-glow transition-all">
          {{ avatarLetter }}
        </router-link>

        <button class="xl:hidden w-10 h-10 rounded-xl grid place-items-center text-ink-2 hover:text-ink hover:bg-white/[.04] transition-all border border-line-strong" @click="$emit('toggle-mobile')">
          <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
        </button>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { h, computed } from 'vue';
import MegaMenu from './MegaMenu.vue';
import IconButton from '@/components/base/IconButton.vue';
import { useAuth } from '@/composables/useAuth';

defineEmits(['toggle-mobile']);

const { user } = useAuth();
const avatarLetter = computed(() => (user.value?.display_name?.[0] || user.value?.email?.[0] || 'M').toUpperCase());

const HomeIcon = () => h('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 2 }, [
  h('rect', { x: 3, y: 3, width: 7, height: 7, rx: 1 }),
  h('rect', { x: 14, y: 3, width: 7, height: 7, rx: 1 }),
  h('rect', { x: 3, y: 14, width: 7, height: 7, rx: 1 }),
  h('rect', { x: 14, y: 14, width: 7, height: 7, rx: 1 }),
]);
const HoursIcon = () => h('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 2 }, [
  h('circle', { cx: 12, cy: 12, r: 9 }),
  h('polyline', { points: '12 7 12 12 15 14' }),
]);
const AiIcon = () => h('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 2 }, [
  h('circle', { cx: 12, cy: 12, r: 9 }),
  h('path', { d: 'M9 11a3 3 0 1 1 5.12 2.12L12 15' }),
  h('line', { x1: 12, y1: 18, x2: 12.01, y2: 18 }),
]);

const items = [
  { to: '/', label: 'Tableau de bord', icon: HomeIcon },
  { to: '/heures', label: 'Mes heures', icon: HoursIcon },
  { to: '/ia', label: 'Assistant IA', icon: AiIcon },
];

const demarchesItems = [
  { to: '/titre', label: 'Titre de sejour', hint: 'Renouvellement, statut', tone: 'cyan' },
  { to: '/caf', label: 'CAF & APL', hint: 'Dossier, justificatifs', tone: 'purple' },
  { to: '/impots', label: 'Impots', hint: 'Declaration, convention', tone: 'cyan' },
  { to: '/sante', label: 'Sante & Secu', hint: 'Ameli, mutuelle', tone: 'purple' },
  { to: '/banque', label: 'Banque', hint: 'Sans bail, sans historique', tone: 'cyan' },
  { to: '/documents', label: 'Documents', hint: 'Coffre-fort, generation', tone: 'purple' },
];

const vieItems = [
  { to: '/logement', label: 'Logement', hint: 'Visale, garant, bail', tone: 'cyan' },
  { to: '/transport', label: 'Transport', hint: 'Navigo, TER, permis', tone: 'purple' },
  { to: '/telecom', label: 'Telephone & Internet', hint: 'Forfaits sans engagement', tone: 'cyan' },
  { to: '/energie', label: 'Energie', hint: 'EDF, Engie sans historique', tone: 'purple' },
];
</script>

<style scoped>
.nav-link {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 12px; border-radius: 10px;
  color: var(--ink-2); font-size: 14px; font-weight: 500;
  transition: all .15s ease;
}
.nav-link:hover { color: var(--ink); background: rgba(255,255,255,.04); }
.nav-link-active {
  background: rgba(34,211,238,.08); color: var(--cyan-light);
  border: 1px solid rgba(34,211,238,.2);
}
.cmdk-trigger {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 12px; border-radius: 10px;
  background: rgba(255,255,255,.04); border: 1px solid var(--line-strong);
  color: var(--muted); font-size: 13px; min-width: 200px;
  transition: all .15s ease;
}
.cmdk-trigger:hover { border-color: var(--cyan); color: var(--ink); }
.kbd {
  font-size: 10px; padding: 2px 6px; border-radius: 4px;
  background: rgba(255,255,255,.08); border: 1px solid var(--line-strong);
  margin-left: auto;
}
</style>
